import { Link } from "@tanstack/react-router";
import { CharacterImage } from "@/components/CharacterImage";
import { ScrollReveal } from "@/components/landing/ScrollReveal";

type Character = {
  code: "jad" | "julia" | "anas" | "talia" | "yousef" | "luna";
  nameEn: string;
  nameAr: string;
  taglineEn: string;
  taglineAr: string;
  color: string;
};

type Group = {
  labelEn: string;
  labelAr: string;
  kickerEn: string;
  kickerAr: string;
  characters: Character[];
};

const groups: Group[] = [
  {
    kickerEn: "Ages 4–6 · Buds",
    kickerAr: "٤–٦ سنوات · براعم",
    labelEn: "First friends",
    labelAr: "أول الأصدقاء",
    characters: [
      {
        code: "jad",
        nameEn: "Jad",
        nameAr: "جاد",
        taglineEn: "Curious explorer",
        taglineAr: "مستكشف فضولي",
        color: "#f59e0b",
      },
      {
        code: "julia",
        nameEn: "Julia",
        nameAr: "جوليا",
        taglineEn: "Kindness & creativity",
        taglineAr: "لطف وإبداع",
        color: "#ec4899",
      },
    ],
  },
  {
    kickerEn: "Ages 7–9 · Branches",
    kickerAr: "٧–٩ سنوات · أغصان",
    labelEn: "Growing minds",
    labelAr: "عقول تنمو",
    characters: [
      {
        code: "anas",
        nameEn: "Anas",
        nameAr: "أنس",
        taglineEn: "Thoughtful problem solver",
        taglineAr: "مفكّر يحلّ المشكلات",
        color: "#10b981",
      },
      {
        code: "talia",
        nameEn: "Talia",
        nameAr: "تاليا",
        taglineEn: "Confidence & empathy",
        taglineAr: "ثقة وتعاطف",
        color: "#8b5cf6",
      },
    ],
  },
  {
    kickerEn: "Ages 10–12 · Fruits",
    kickerAr: "١٠–١٢ سنة · ثمار",
    labelEn: "Older friends",
    labelAr: "أصدقاء أكبر",
    characters: [
      {
        code: "yousef",
        nameEn: "Yousef",
        nameAr: "يوسف",
        taglineEn: "Calm strength & quiet courage",
        taglineAr: "قوة هادئة وشجاعة صامتة",
        color: "#7a8c5c",
      },
      {
        code: "luna",
        nameEn: "Luna",
        nameAr: "لونا",
        taglineEn: "Reflection & warm wisdom",
        taglineAr: "تأمّل ودفء وحكمة",
        color: "#b8694b",
      },
    ],
  },
];

export function CharacterCast({ isAr }: { isAr: boolean }) {
  return (
    <section
      id="cast"
      aria-labelledby="cast-heading"
      className="px-5 py-20 bg-gradient-to-b from-background via-clay/10 to-background"
    >
      <div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <p className="text-center font-display-en text-xs md:text-sm mb-3 uppercase tracking-[0.3em] text-primary font-bold">
            {isAr ? "شخصيات جذوري" : "Meet the cast"}
          </p>
          <h2
            id="cast-heading"
            className={`text-center mb-4 tracking-tight ${
              isAr
                ? "font-display-ar text-4xl md:text-5xl text-primary"
                : "font-display-en text-3xl md:text-4xl"
            }`}
          >
            {isAr ? "أصدقاء يكبرون مع طفلك" : "Friends who grow with your child"}
          </h2>
          <p
            className={`text-center max-w-2xl mx-auto text-foreground/65 mb-14 ${
              isAr ? "font-display-ar text-lg" : ""
            }`}
            dir={isAr ? "rtl" : "ltr"}
          >
            {isAr
              ? "كل شخصية تحمل قيمة وروحًا — يلتقي بها طفلك في القصص والفيديوهات والمكافآت، فتتحوّل إلى ذكرى يحبّها."
              : "Each character carries a value and a spirit — your child meets them in stories, videos, and rewards, and they become friends for life."}
          </p>
        </ScrollReveal>

        <div className="space-y-10 md:space-y-12">
          {groups.map((g, gi) => (
            <ScrollReveal key={g.kickerEn} delay={gi * 120}>
              <div className="rounded-[2rem] bg-card border-2 border-clay/30 p-6 md:p-10 shadow-sm">
                <p
                  className={`text-xs md:text-sm font-black uppercase tracking-[0.25em] mb-1 ${
                    gi === 0
                      ? "text-secondary"
                      : gi === 1
                        ? "text-primary"
                        : "text-accent"
                  }`}
                >
                  {isAr ? g.kickerAr : g.kickerEn}
                </p>
                <h3
                  className={`mb-8 tracking-tight ${
                    isAr
                      ? "font-display-ar text-2xl md:text-3xl text-foreground"
                      : "font-display-en text-2xl md:text-3xl font-extrabold"
                  }`}
                  dir={isAr ? "rtl" : "ltr"}
                >
                  {isAr ? g.labelAr : g.labelEn}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-10">
                  {g.characters.map((c) => (
                    <Link
                      key={c.code}
                      to="/characters/$code"
                      params={{ code: c.code }}
                      className="group flex items-center gap-5 rounded-3xl p-4 md:p-5 -mx-1 hover:bg-clay/15 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <div
                        className="shrink-0 size-24 md:size-28 rounded-3xl overflow-hidden ring-4 ring-offset-2 ring-offset-card transition-transform group-hover:-translate-y-1"
                        style={{
                          background: `linear-gradient(135deg, ${c.color}33, ${c.color}66)`,
                          boxShadow: `0 12px 28px -10px ${c.color}66`,
                        }}
                      >
                        <CharacterImage
                          code={c.code}
                          alt={isAr ? c.nameAr : c.nameEn}
                          className="size-full object-cover"
                          sizes="(min-width: 768px) 112px, 96px"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p
                          className={`leading-none mb-2 ${
                            isAr
                              ? "font-display-ar text-3xl md:text-4xl"
                              : "font-display-en text-2xl md:text-3xl font-black tracking-tight"
                          }`}
                          style={{ color: c.color }}
                          dir={isAr ? "rtl" : "ltr"}
                        >
                          {isAr ? c.nameAr : c.nameEn}
                        </p>
                        <p
                          className={`text-foreground/70 ${
                            isAr ? "font-display-ar text-lg" : "text-sm md:text-base"
                          }`}
                          dir={isAr ? "rtl" : "ltr"}
                        >
                          {isAr ? c.taglineAr : c.taglineEn}
                        </p>
                        <span
                          className="mt-3 inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest opacity-80 group-hover:opacity-100 transition-opacity"
                          style={{ color: c.color }}
                        >
                          {isAr ? `تعرّف على ${c.nameAr}` : `Meet ${c.nameEn}`}
                          <span aria-hidden="true">{isAr ? "←" : "→"}</span>
                        </span>
                      </div>
                    </Link>

                  ))}
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal delay={150}>
          <div className="mt-12 text-center">
            <Link
              to="/characters"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-colors min-h-11"
            >
              {isAr ? "تعرّف على كل الشخصيات" : "Meet every character"}
            </Link>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
